/**
 * Cart Request Queue Manager
 *
 * PURPOSE: Prevents race conditions between simultaneous cart mutations
 * (inscription saves, quantity updates, cart adds, etc.)
 *
 * PROBLEM IT SOLVES:
 * - Multiple POST requests to /cart/change.js can conflict
 * - Last request "wins" regardless of timing
 * - Inscription saves can be overwritten by quantity updates
 * - No coordination between different cart operations
 *
 * SOLUTION:
 * - Serializes all cart mutation requests (one at a time)
 * - Queues pending requests
 * - Supports request cancellation via AbortController
 * - Provides status tracking (pending, active, completed, cancelled)
 */

class CartRequestQueue {
  constructor() {
    this.queue = [];
    this.activeRequest = null;
    this.isProcessing = false;
  }

  /**
   * Add a request to the queue
   * @param {Function} requestFn - Function that returns a Promise (the fetch call)
   * @param {Object} options - { priority: 'high'|'normal', abortController: AbortController }
   * @returns {Promise} - Resolves when request completes
   */
  enqueue(requestFn, options = {}) {
    const { priority = 'normal', abortController = null, id = null } = options;

    return new Promise((resolve, reject) => {
      const request = {
        id: id || `req_${Date.now()}_${Math.random()}`,
        fn: requestFn,
        resolve,
        reject,
        priority,
        abortController,
        status: 'pending',
        createdAt: Date.now(),
      };

      // High priority requests go to front of queue
      if (priority === 'high') {
        this.queue.unshift(request);
      } else {
        this.queue.push(request);
      }

      console.log(`[CartQueue] Enqueued request ${request.id} (priority: ${priority}, queue length: ${this.queue.length})`);

      this.processNext();
    });
  }

  /**
   * Cancel a specific request by ID
   * @param {string} requestId - The request ID to cancel
   */
  cancel(requestId) {
    const request = this.queue.find(r => r.id === requestId);
    if (request) {
      request.status = 'cancelled';
      if (request.abortController) {
        request.abortController.abort();
      }
      request.reject(new Error('Request cancelled'));
      this.queue = this.queue.filter(r => r.id !== requestId);
      console.log(`[CartQueue] Cancelled request ${requestId}`);
    }
  }

  /**
   * Cancel all pending inscription saves
   * (Used when quantity update happens - inscription saves should retry after)
   */
  cancelPendingInscriptionSaves() {
    const inscriptionRequests = this.queue.filter(r => r.id.startsWith('inscription_'));
    inscriptionRequests.forEach(req => this.cancel(req.id));
    console.log(`[CartQueue] Cancelled ${inscriptionRequests.length} pending inscription saves`);
  }

  /**
   * Process the next request in the queue
   */
  async processNext() {
    // Already processing or queue is empty
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const request = this.queue.shift();

    // Skip if already cancelled
    if (request.status === 'cancelled') {
      this.isProcessing = false;
      this.processNext();
      return;
    }

    this.activeRequest = request;
    request.status = 'active';

    console.log(`[CartQueue] Processing request ${request.id} (${this.queue.length} remaining in queue)`);

    try {
      const result = await request.fn();
      request.status = 'completed';
      request.resolve(result);
      console.log(`[CartQueue] Completed request ${request.id}`);
    } catch (error) {
      request.status = 'failed';
      request.reject(error);
      console.error(`[CartQueue] Failed request ${request.id}:`, error);
    } finally {
      this.activeRequest = null;
      this.isProcessing = false;

      // Process next request in queue
      if (this.queue.length > 0) {
        this.processNext();
      }
    }
  }

  /**
   * Get current queue status (for debugging)
   */
  getStatus() {
    return {
      queueLength: this.queue.length,
      activeRequest: this.activeRequest ? this.activeRequest.id : null,
      isProcessing: this.isProcessing,
      pendingRequests: this.queue.map(r => ({
        id: r.id,
        priority: r.priority,
        status: r.status,
        age: Date.now() - r.createdAt,
      })),
    };
  }
}

// Create global singleton instance
window.cartRequestQueue = new window.cartRequestQueue || new CartRequestQueue();

// Expose for debugging
window.getCartQueueStatus = () => window.cartRequestQueue.getStatus();
